import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { CowreMark, IconClose, IconPlus } from '../../../common/icons';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { formatDateOnly } from '../../../common/utils/date';
import { TE_ROLE_LABEL } from '../../../core/constants/transfers';
import { useAvailableEmbryos } from '../../embryos/contexts/embryos';
import { useTransferSession, useOpuTransferSessions } from '../contexts/transfers';
import { transferKeys } from '../queries/keys';
import { transferPaths } from '../queries/paths';
import { buildTransferredRows, enrichRows, mergeBoardRows } from '../utils/board';
import TransferRows from '../forms/transfer-rows';
import BankPicker from '../forms/bank-picker';
import SessionLegend from './session-legend';

import './styles/workspace.css';

function buildRemarks(remarks) {
    const out = {};
    if (remarks?.corpusLuteum) out.corpusLuteum = remarks.corpusLuteum;
    if (remarks?.details) out.details = remarks.details;
    return Object.keys(out).length ? out : null;
}

// Reanudar una sesión TE = tablero de la OPU: la lista de embriones NO muta
// (modelo de papel). Las ya transferidas quedan en la tabla, subrayadas por el
// color de su sesión (toggle para ocultarlas); a las disponibles se les asigna
// receptora y se agregan a ESTA sesión vía nested POST /:id/transfers.
export default function TransferEditWorkspace() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [params] = useSearchParams();
    const teId = params.get('session');
    const opuId = params.get('opu');

    const { data: te, isLoading, error } = useTransferSession(teId);
    const { data: embryosData, error: embryosError } = useAvailableEmbryos({ opu: opuId, limit: 500 }, { enabled: !!opuId });
    const { data: opuData } = useOpuTransferSessions(opuId, { enabled: !!opuId });

    // Detalle assembled de CADA sesión TE de la OPU: el endpoint derivado no trae
    // remarks (CL/OBS) ni linaje, así que hidratamos el tablero completo con el DTO
    // directo de cada sesión. Dedupe con useTransferSession (misma queryKey/cache).
    const sessionIds = (opuData?.transfer_sessions || []).map((s) => s.id);
    const sessionDetailQueries = useQueries({
        queries: sessionIds.map((id) => ({
            queryKey: transferKeys.detail(id),
            queryFn: ({ signal }) => api.get(transferPaths.session(id), { signal }).then((r) => r.data),
            enabled: !!id,
            staleTime: 30_000,
        })),
    });

    const [rows, setRows] = useState(null);
    const [seeded, setSeeded] = useState(false);
    const [showBank, setShowBank] = useState(false);
    const [hideTransferred, setHideTransferred] = useState(false);
    const [saving, setSaving] = useState(false);
    const confirm = useConfirm();

    if (embryosData && !seeded) {
        setRows((embryosData.items || []).map(e => ({ key: `e${e.id}`, embryo: e, source: 'opu', recipient: null, remarks: {} })));
        setSeeded(true);
    }

    if (isLoading || (!te && !error)) return <div className="transfer-ws__state">Cargando sesión TE…</div>;
    if (error || !te) return <div className="transfer-ws__state">Sesión de transferencia no encontrada.</div>;

    const list = rows || [];
    const setRecipient = (key, rec) => setRows(prev => prev.map(r => (r.key === key ? { ...r, recipient: rec } : r)));
    const clearRecipient = (key) => setRows(prev => prev.map(r => (r.key === key ? { ...r, recipient: null } : r)));
    const setCL = (key, cl) => setRows(prev => prev.map(r => (r.key === key ? { ...r, remarks: { ...r.remarks, corpusLuteum: cl } } : r)));
    const setObs = (key, d) => setRows(prev => prev.map(r => (r.key === key ? { ...r, remarks: { ...r.remarks, details: d } } : r)));
    const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));
    const addBank = (embryos) => setRows(prev => {
        const have = new Set(prev.map(r => r.embryo.id));
        const fresh = embryos.filter(e => !have.has(e.id));
        return [...prev, ...fresh.map(e => ({ key: `e${e.id}`, embryo: e, source: 'bank', recipient: null, remarks: {} }))];
    });

    // Tablero único (modelo de papel): TODAS las transferencias de la OPU, cada
    // una subrayada con el color de su sesión. Se enriquece cada fila con el DTO
    // directo de su sesión (byId global sobre todas las sesiones) → remarks (CL/Obs)
    // y linaje quedan disponibles/editables en cualquier sesión, no solo la reanudada.
    // Las filas con preñez `open` además permiten cambiar receptora / deshacer la TE.
    const byId = new Map();
    sessionDetailQueries.forEach((q) => (q.data?.transfers || []).forEach((t) => byId.set(t.id, t)));
    (te.transfers || []).forEach((t) => { if (!byId.has(t.id)) byId.set(t.id, t); });
    const transferredRows = enrichRows(buildTransferredRows(opuData), byId);
    const display = mergeBoardRows(list, hideTransferred ? [] : transferredRows);
    const existingIds = new Set([...list.map(r => r.embryo.id), ...transferredRows.map(r => r.embryo.id)]);
    const assigned = list.filter(r => r.recipient);

    const backUrl = opuId ? `/admin/opu/${opuId}` : '/admin/opu';
    const changeSession = () => navigate(opuId ? `/opu/transfer?session=${opuId}` : backUrl);

    // Edición/deshacer de una transferencia YA registrada (de cualquier sesión de
    // la OPU). Restricciones del backend (admin-transfer-sessions.md): receptora y
    // delete solo si la preñez está `open` (422/409 si progresó). Se opera contra
    // la sesión dueña de la fila (row.sessionId), no necesariamente la reanudada.
    const invalidateBoard = () => {
        qc.invalidateQueries({ queryKey: transferKeys.all });
        if (opuId) qc.invalidateQueries({ queryKey: ['transfer-sessions', 'opu', opuId] });
        qc.invalidateQueries({ queryKey: ['opu'] });
    };
    const patchTransfer = async (row, body, okMsg) => {
        try {
            await api.patch(transferPaths.transferItem(row.sessionId, row.transferId), body);
            invalidateBoard();
            if (okMsg) toast.success(okMsg);
        } catch (err) {
            if (err?.status === 422) toast.error(err?.message || 'No editable: la preñez ya progresó');
            else if (err?.status === 409) toast.error(err?.message || 'Conflicto al editar la transferencia');
            else toast.error(err?.message || 'No se pudo editar la transferencia');
        }
    };
    const editRecipient = (row, rec) => patchTransfer(row, { recipient_id: rec.id }, 'Receptora actualizada');
    const editRemarks = (row, remarks) => patchTransfer(row, { remarks });
    const deleteTransfer = async (row) => {
        const ok = await confirm({
            title: 'Deshacer transferencia',
            message: `¿Deshacer la transferencia de ${row.embryo?.code || 'este embrión'}?`,
            confirmLabel: 'Deshacer',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(transferPaths.transferItem(row.sessionId, row.transferId));
            invalidateBoard();
            toast.success('Transferencia deshecha');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede deshacer: la preñez ya progresó');
            else toast.error(err?.message || 'No se pudo deshacer la transferencia');
        }
    };

    const handleSave = async () => {
        if (!assigned.length) { toast.error('Asigná una receptora a los embriones que querés agregar'); return; }
        setSaving(true);
        const failures = [];
        let attempted = 0;
        const run = async (label, fn) => { attempted++; try { await fn(); } catch (err) { failures.push(err?.message ? `${label} (${err.message})` : label); } };

        for (const r of assigned) {
            const remarks = buildRemarks(r.remarks);
            const body = { embryo_id: r.embryo.id, recipient_id: r.recipient.id, ...(remarks ? { remarks } : {}) };
            await run(`embrión ${r.embryo.code}`, () => api.post(transferPaths.transfers(teId), body));
        }

        setSaving(false);
        qc.invalidateQueries({ queryKey: transferKeys.all });
        if (opuId) qc.invalidateQueries({ queryKey: ['transfer-sessions', 'opu', opuId] });
        qc.invalidateQueries({ queryKey: ['opu'] });
        if (attempted > 0 && failures.length === attempted) { toast.error(`No se pudo agregar: ${failures.join(' · ')}`); return; }
        if (failures.length) toast.error(`Agregado con avisos: ${failures.join(' · ')}`);
        else toast.success(`${assigned.length} transferencia(s) agregada(s)`);
        navigate(`/admin/transfer/${teId}${opuId ? `?opu=${opuId}` : ''}`);
    };

    return (
        <div className="transfer-ws">
            <header className="transfer-ws__topbar">
                <div className="transfer-ws__brand">
                    <span className="transfer-ws__mark"><CowreMark size={30} id="te-edit-mark" /></span>
                    <span className="transfer-ws__brand-text">COWRE</span>
                    <span className="transfer-ws__divider" />
                    <span className="transfer-ws__title">Reanudar · {te.code}</span>
                </div>
                <button type="button" className="transfer-ws__close" onClick={() => navigate(backUrl)} aria-label="Cerrar"><IconClose /></button>
            </header>

            <div className="transfer-ws__content">
                <section className="transfer-ws__summary">
                    <div className="transfer-ws__summary-main">
                        <span className="transfer-summary__farm">{te.farm?.name || '—'}</span>
                        <span className="transfer-summary__sub">
                            {formatDateOnly(te.date)}
                            {(te.staff?.length > 0) && ` · ${te.staff.map(s => `${s.user?.name} (${TE_ROLE_LABEL[s.role] || s.role})`).join(', ')}`}
                        </span>
                    </div>
                    <div className="transfer-ws__summary-actions">
                        <button type="button" className="transfer-ws__switch" onClick={changeSession} disabled={saving}>Cambiar sesión</button>
                        <button type="button" className="transfer-ws__bank" onClick={() => setShowBank(true)} disabled={saving}>
                            <IconPlus /><span>Agregar del banco</span>
                        </button>
                    </div>
                </section>

                <section className="transfer-add">
                    <div className="transfer-add__head">
                        <h2 className="transfer-section__title">Transferencias de la OPU</h2>
                        {transferredRows.length > 0 && (
                            <label className="transfer-ws__toggle">
                                <input type="checkbox" checked={hideTransferred} onChange={(e) => setHideTransferred(e.target.checked)} />
                                <span>Ocultar transferidas</span>
                            </label>
                        )}
                    </div>

                    {!hideTransferred && transferredRows.length > 0 && <SessionLegend sessions={opuData?.transfer_sessions} />}

                    {embryosError && (
                        <div className="transfer-ws__error">
                            {embryosError.status === 403 ? 'No tenés permiso para ver los embriones (embryos:read).' : 'No se pudieron cargar los embriones disponibles.'}
                        </div>
                    )}

                    {!display.length ? (
                        <div className="transfer-ws__state">No hay embriones de esta OPU. Podés agregar del banco.</div>
                    ) : (
                        <TransferRows
                            rows={display}
                            farmId={te.farm?.id}
                            onRecipient={setRecipient}
                            onClearRecipient={clearRecipient}
                            onCL={setCL}
                            onObs={setObs}
                            onRemove={removeRow}
                            onEditRecipient={editRecipient}
                            onEditRemarks={editRemarks}
                            onDeleteTransfer={deleteTransfer}
                        />
                    )}
                </section>
            </div>

            <footer className="transfer-ws__footer">
                <span className="transfer-ws__footer-hint">{assigned.length} transferencia(s) por agregar</span>
                <div className="transfer-ws__actions">
                    <button type="button" className="transfer-ws__cancel" onClick={() => navigate(backUrl)} disabled={saving}>Cerrar</button>
                    <button type="button" className="transfer-ws__primary" onClick={handleSave} disabled={saving || !assigned.length}>
                        {saving ? 'Agregando…' : 'Agregar transferencias'}
                    </button>
                </div>
            </footer>

            {showBank && <BankPicker existingIds={existingIds} onAdd={addBank} onClose={() => setShowBank(false)} />}
        </div>
    );
}
